/**
 * Processed-EEG (depth of anaesthesia) engine.
 *
 * One hypnotic-depth index drives everything: the store's `bisValue` (0–100,
 * set by the intervention panel from the sedatives/volatiles given). From it
 * we derive what each commercial monitor would show —
 *   • BIS™ (Medtronic/Covidien): BIS, SR, EMG, SQI, SEF
 *   • Entropy (GE): State Entropy (SE), Response Entropy (RE), BSR
 *   • SedLine® (Masimo): PSi, SR, EMG, SEF, ARTF, plus a 4-channel DSA
 * — and a synthetic raw EEG whose spectral content follows the depth:
 * awake = low-amplitude fast β; light GA = α spindles + θ; deep = large slow
 * δ; very deep (index < ~25) = burst suppression.
 *
 * Pure functions, deterministic for a given (index, t) so every monitor
 * window draws the same trace.
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const lerp = (a, b, t) => a + (b - a) * t;

/** Relative power of each EEG band (δ θ α β) at a given depth index. */
export function bandWeights(index) {
  const x = clamp(index, 0, 100) / 100;          // 0 = isoelectric … 1 = awake
  // δ dominates deep anaesthesia, α peaks in the surgical range (40–60),
  // β/γ dominate the awake state.
  const delta = lerp(1.0, 0.08, x);
  const alpha = Math.exp(-((x - 0.5) ** 2) / 0.045) * 0.9;
  const theta = lerp(0.5, 0.15, x) + alpha * 0.3;
  const beta = lerp(0.05, 1.0, x ** 1.6);
  return { delta, theta, alpha, beta };
}

/** Suppression ratio (% of the last 63 s that is isoelectric) from the index. */
export function suppressionRatio(index) {
  if (index >= 30) return 0;
  return Math.round(clamp((30 - index) / 30, 0, 1) * 100);
}

/** Everything the three monitors display, from one depth index. */
export function eegDerived(index) {
  const i = clamp(Math.round(index), 0, 100);
  const sr = suppressionRatio(i);
  // EMG (dB) — frontalis tone falls as the patient goes under.
  const emg = Math.round(lerp(26, 48, i / 100));
  // Spectral edge frequency 95 % (Hz).
  const sef = +(lerp(6, 28, (i / 100) ** 1.2)).toFixed(1);
  // GE Entropy: SE (0–91) tracks the hypnotic state; RE (0–100) adds the
  // fast (EMG) component, so RE > SE when the patient is light / responding.
  const se = Math.round(clamp(i * 0.91, 0, 91));
  const reGap = i > 70 ? 8 : i > 50 ? 4 : i > 30 ? 2 : 1;
  const re = Math.round(clamp(se + reGap, 0, 100));
  // Masimo PSi (25–50 surgical range) sits a touch below BIS at the same depth.
  const psi = Math.round(clamp(i * 0.95 + 1, 0, 100));
  return {
    bis: i, sr, emg, sqi: i < 5 ? 70 : 98, sef,
    se, re, bsr: sr,
    psi, artf: 0,
  };
}

// ── Raw EEG synthesis ───────────────────────────────────────────────────────
// Deterministic pseudo-random per (seed, k) for phases / burst gating.
function hash(seed, k) {
  let h = (seed * 374761393 + k * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const COMPONENTS = [
  // [band, frequency Hz, relative amplitude]
  ['delta', 1.2, 1.0], ['delta', 2.3, 0.7], ['delta', 3.4, 0.5],
  ['theta', 4.8, 0.6], ['theta', 6.5, 0.5], ['theta', 7.6, 0.4],
  ['alpha', 9.5, 0.7], ['alpha', 11.0, 0.9], ['alpha', 12.5, 0.6],
  ['beta', 15, 0.5], ['beta', 19, 0.45], ['beta', 24, 0.4], ['beta', 29, 0.3],
];

/**
 * Raw EEG amplitude (µV, roughly ±60) at time t seconds for a depth index.
 * `channel` offsets the phases so SedLine's four leads differ but correlate.
 */
export function eegAt(t, index, channel = 0) {
  const w = bandWeights(index);
  let v = 0;
  for (let k = 0; k < COMPONENTS.length; k++) {
    const [band, f, a] = COMPONENTS[k];
    const phase = hash(11 + channel, k) * Math.PI * 2;
    // Slow amplitude modulation so the trace "breathes" like real EEG.
    const am = 0.7 + 0.3 * Math.sin(t * (0.13 + k * 0.021) + phase * 3);
    v += w[band] * a * am * Math.sin(2 * Math.PI * f * t + phase);
  }
  // Broadband noise, larger when awake (muscle).
  const n = (hash(97 + channel, Math.floor(t * 500)) - 0.5) * (0.25 + 0.5 * (index / 100));
  v += n;
  // Amplitude: awake EEG is small (~15 µV), deep slow-wave is large (~60 µV).
  const amp = lerp(60, 14, clamp(index, 0, 100) / 100);
  v *= amp / 2.2;
  // Burst suppression below ~25: isoelectric windows whose share equals SR.
  const sr = suppressionRatio(index) / 100;
  if (sr > 0) {
    const slot = Math.floor(t / 1.5);                     // 1.5 s epochs
    const gate = hash(5 + channel, slot) < sr ? 0.04 : 1; // suppressed epoch → near-flat
    // Soften the edges of each epoch so bursts don't start with a click.
    const frac = (t / 1.5) % 1;
    const edge = Math.min(1, Math.min(frac, 1 - frac) * 10);
    v *= gate === 1 ? 1 : lerp(1, gate, edge);
  }
  // Isoelectric at index 0.
  if (index <= 0) v *= 0.02;
  return v;
}

/**
 * Power per 1-Hz bin from 0.5 to 30 Hz (dB-like, 0..1 normalised) for the
 * density spectral array; a column of the SedLine DSA is one call.
 */
export function dsaColumn(index, bins = 30) {
  const w = bandWeights(index);
  const out = new Float32Array(bins);
  const centres = { delta: 2, theta: 6, alpha: 10.5, beta: 20 };
  const widths = { delta: 2.2, theta: 2.2, alpha: 2.6, beta: 9 };
  for (let b = 0; b < bins; b++) {
    const f = b + 0.5;
    let p = 0.02;
    for (const band of Object.keys(centres)) {
      p += w[band] * Math.exp(-((f - centres[band]) ** 2) / (2 * widths[band] ** 2));
    }
    // 1/f tilt + suppression dims everything.
    p *= 1 / (1 + f * 0.03);
    p *= 1 - suppressionRatio(index) / 100 * 0.85;
    out[b] = clamp(p, 0, 1);
  }
  return out;
}

/** Brand presentation metadata. */
export const EEG_BRANDS = {
  bis: {
    label: 'BIS™', vendor: 'Bispectral Index', index: 'BIS', range: '40–60 general anaesthesia',
    accent: '#4FC3F7', trace: '#E8F4FF',
  },
  entropy: {
    label: 'Entropy', vendor: 'GE Carescape · E-ENTROPY', index: 'SE', range: '40–60 adequate anaesthesia',
    accent: '#FFD600', trace: '#E0E0E0',
  },
  sedline: {
    label: 'SedLine®', vendor: 'Masimo Root · Patient State Index', index: 'PSi', range: '25–50 adequate anaesthesia',
    accent: '#FF8A3D', trace: '#C9B6FF',
  },
};
export const EEG_BRAND_KEYS = Object.keys(EEG_BRANDS);
